// The language switcher (D70), on the sign-in page, in the header (S113) and in the profile menu.
// Each language is named in itself, beside its country's flag, so a reader who landed in the
// wrong one still finds theirs.
import { Check } from "@phosphor-icons/react";
import { Dropdown } from "rizzui";

import { LOCALE_INFO, LOCALES, type Locale, setLocale, useLocale, useT } from "../i18n";
import { cn } from "./cn";
import { Flag } from "./Flag";

/** The languages as a list of choices, for a menu that already has a frame. */
export function LanguageOptions({ onChosen }: { onChosen?: () => void }) {
  const current = useLocale();
  const choose = (l: Locale) => {
    void setLocale(l);
    onChosen?.();
  };
  return (
    <>
      {LOCALES.map((l) => (
        <Dropdown.Item key={l} className="justify-between rounded-md" lang={l} onClick={() => choose(l)} aria-current={l === current ? "true" : undefined}>
          <span className="flex items-center gap-2">
            <Flag locale={l} />
            {LOCALE_INFO[l].name}
          </span>
          {l === current ? <Check weight="bold" className="h-4 w-4 text-primary" aria-hidden /> : null}
        </Dropdown.Item>
      ))}
    </>
  );
}

/** A compact button showing the active language, opening the choices. */
export function LanguageMenu({ className }: { className?: string }) {
  const t = useT();
  const current = useLocale();
  return (
    <Dropdown placement="bottom-end">
      <Dropdown.Trigger
        aria-label={`${t.common.language.choose}: ${LOCALE_INFO[current].name}`}
        className={cn(
          "inline-flex h-[34px] items-center gap-1.5 rounded-full bg-gray-100 px-3 text-[13px] font-medium uppercase text-gray-700 hover:text-gray-1000",
          className,
        )}
      >
        <Flag locale={current} />
        {current}
      </Dropdown.Trigger>
      <Dropdown.Menu className="w-44 dark:bg-gray-100">
        <LanguageOptions />
      </Dropdown.Menu>
    </Dropdown>
  );
}

/** The header's round language button (S113): the current language's flag, opening the choices. */
export function HeaderLanguageMenu({ className }: { className?: string }) {
  const t = useT();
  const current = useLocale();
  return (
    <Dropdown placement="bottom-end">
      <Dropdown.Trigger
        aria-label={`${t.common.language.choose}: ${LOCALE_INFO[current].name}`}
        className={cn("flex h-[34px] w-[34px] items-center justify-center rounded-full bg-gray-100 hover:ring-2 hover:ring-primary/30 dark:bg-gray-100", className)}
      >
        <Flag locale={current} size={20} />
      </Dropdown.Trigger>
      <Dropdown.Menu className="w-44 dark:bg-gray-100">
        <div className="px-3 pb-1 pt-2 text-xs font-medium text-gray-500">{t.common.language.label}</div>
        <LanguageOptions />
      </Dropdown.Menu>
    </Dropdown>
  );
}
